/**
 * AgentGallery — the home-page agent picker.
 *
 * Replaces the horizontal carousel. The problem it solves (Justin & Shuki):
 * a side-scrolling strip hid agents, so people forgot what they had seen and
 * never learned what each agent was for. Everything is on screen at once
 * ("פרוסים לעין"), with capability detail on hover.
 *
 * ONE INTERACTION: a card is a door. Clicking any card — Concierge or agent —
 * opens a new chat with it immediately. There is no "select, then type here"
 * step and no persistent selected state, because this page has no input of
 * its own; picking IS starting.
 *
 * COLOUR LIVES IN THE ARC, NOT THE CARD. The cards are flat white. Hovering
 * one draws a gradient edge in that agent's colour and — the loud part —
 * fades the hero semicircle above the grid to the same colour. That keeps the
 * grid calm while still making every agent's identity felt, and it is why
 * this component reports hover upward via `onHoverAgent`.
 *
 * The Concierge is always the first card and is styled by the concierge
 * module (ConciergeGalleryCard.css).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { BotAvatar } from '@/components/common/BotAvatar';
import { resolveAgentTextureColor } from '@/brand/textures';
import { useLanguage } from '@/hooks/useLanguage';
import { isConciergeCarouselBot } from '@modules/concierge/components/conciergeCarouselItem';
import {
  ConciergeCardFace,
  CONCIERGE_GALLERY_CARD_CLASS,
} from '@modules/concierge/components/ConciergeGalleryCard';
import { useAllAgentSkills } from './useAgentSkills';
import type { AgentGalleryProps } from './types';
import './AgentGallery.css';

type CategoryTab = 'all' | 'favorites' | 'recent';

const TABS: CategoryTab[] = ['all', 'favorites', 'recent'];

/**
 * Below this many agents the tabs are noise — every agent is already visible
 * in one glance, which is the case for most tenants.
 */
const MIN_AGENTS_FOR_TABS = 5;

export function AgentGallery({
  bots,
  onOpenAgent,
  onHoverAgent,
  onToggleFavorite,
  favoriteIds,
  recentIds,
}: AgentGalleryProps) {
  const { t } = useLanguage();
  const [tab, setTab] = useState<CategoryTab>('all');

  const concierge = useMemo(
    () => bots.find(bot => isConciergeCarouselBot(bot)) || null,
    [bots],
  );
  const agents = useMemo(
    () => bots.filter(bot => !isConciergeCarouselBot(bot)),
    [bots],
  );

  const agentIds = useMemo(() => agents.map(bot => bot.chatbot_id), [agents]);
  // Operator decision 2026-08-20: the gallery must NOT reveal which skills
  // are linked to an agent — no hover swap, no touch tags. `enabled: false`
  // also stops the per-agent /skills requests the display no longer needs.
  useAllAgentSkills(agentIds, false);

  const favoriteCount = agents.filter(bot => favoriteIds.has(bot.chatbot_id)).length;
  const recentCount = agents.filter(bot => recentIds.has(bot.chatbot_id)).length;

  // Tabs earn their space only on a roster big enough to need filtering AND
  // where at least one filter would actually do something.
  const showTabs =
    agents.length >= MIN_AGENTS_FOR_TABS && (favoriteCount > 0 || recentCount > 0);
  const activeTab = showTabs ? tab : 'all';

  // Sticky-state detection for the tab bar. The bar paints no backdrop of
  // its own; only while it is actually PINNED (cards scrolling under it)
  // does CSS add one (see `.ag-tabbar.is-stuck::before`). A sentinel sits on
  // the strip the bar overhangs above this element; once it leaves the
  // scrollport the bar has pinned. IntersectionObserver clips through every
  // scrolling ancestor, so no root or scroll listener is needed.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [tabbarStuck, setTabbarStuck] = useState(false);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setTabbarStuck(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showTabs]);

  const visible = useMemo(() => {
    if (activeTab === 'favorites') {
      return agents.filter(bot => favoriteIds.has(bot.chatbot_id));
    }
    if (activeTab === 'recent') {
      return agents.filter(bot => recentIds.has(bot.chatbot_id));
    }
    return agents;
  }, [agents, activeTab, favoriteIds, recentIds]);

  const tabCounts: Record<CategoryTab, number> = {
    all: agents.length,
    favorites: favoriteCount,
    recent: recentCount,
  };

  return (
    <div
      className="ag-root"
      data-testid="agent-gallery"
      onMouseLeave={() => onHoverAgent(null)}
    >
      {/* Sticky bar, not a bare pill: the filter has to stay reachable while
          a long roster scrolls underneath. At rest it paints nothing — it
          sits on the hero band (or the plain page) as-is; only while pinned
          does `is-stuck` give it an opaque, full-bleed backdrop so cards
          cannot show through as they pass. */}
      {showTabs && (
        <div ref={sentinelRef} className="ag-tabbar-sentinel" aria-hidden="true" />
      )}
      {showTabs && (
        <div className={`ag-tabbar ${tabbarStuck ? 'is-stuck' : ''}`}>
          <div className="ag-tabs" role="tablist" aria-label={t('dashboard.agents.tabs.label')}>
            {TABS.map(key => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                className={`ag-tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => setTab(key)}
              >
                {t(`dashboard.agents.tabs.${key}`)}
                <span className="ag-tab-count">{tabCounts[key]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ag-grid" role="list">
        {/* Concierge leads the "All" tab — it is the way in for anyone who
            doesn't already know which agent they want. It is NOT itself a
            real agent, so it has no favorite/recent state of its own and
            has no business appearing when those tabs are filtered to. */}
        {concierge && activeTab === 'all' && (
          <div className="ag-card-wrap" role="listitem">
            <button
              type="button"
              className={`ag-card ${CONCIERGE_GALLERY_CARD_CLASS}`}
              onClick={() => onOpenAgent(concierge)}
              onMouseEnter={() => onHoverAgent(null)}
            >
              <ConciergeCardFace />
            </button>
          </div>
        )}

        {visible.map(bot => {
          const color = resolveAgentTextureColor(bot.avatar_color, bot.chatbot_name || '');
          const isFavorite = favoriteIds.has(bot.chatbot_id);

          return (
            <div
              className="ag-card-wrap"
              role="listitem"
              key={bot.chatbot_id}
              data-agent-color={color}
              onMouseEnter={() => onHoverAgent(color)}
            >
              <button
                type="button"
                className="ag-card"
                data-testid="agent-card"
                data-agent-color={color}
                onClick={() => onOpenAgent(bot)}
              >
                {/* Rest state: coin + name only, centred. Hover crossfades
                    this OUT and the full description IN (see .ag-card-desc
                    below) — the description takes over the whole card
                    instead of opening a separate floating bubble. */}
                <span className="ag-card-face">
                  <span className="ag-card-avatar agent-tile-avatar">
                    <BotAvatar
                      name={bot.chatbot_name || 'AI'}
                      size={72}
                      colorOverride={bot.avatar_color}
                      variantOverride={bot.avatar_variant ?? undefined}
                    />
                  </span>
                  {/* dir="auto": tenant data keeps its own direction inside
                      a Hebrew page (and vice versa) — carried over from the
                      bubble this face replaced. */}
                  <span className="ag-card-name" dir="auto">{bot.chatbot_name}</span>
                </span>

                {bot.chatbot_description && (
                  <span className="ag-card-desc" dir="auto">{bot.chatbot_description}</span>
                )}
              </button>

              {/* Favourite toggle. Deliberately a SIBLING of the card button,
                  not a child: a button inside a button is invalid HTML and
                  browsers resolve the nesting unpredictably. Absolutely
                  positioned over the card's top corner instead.
                  Always in the DOM (screen readers and keyboard users need
                  it); CSS reveals it on hover/focus for pointer users, and
                  keeps it permanently visible once an agent IS a favourite. */}
              <button
                type="button"
                className={`ag-fav ${isFavorite ? 'is-fav' : ''}`}
                aria-pressed={isFavorite}
                aria-label={t(
                  isFavorite
                    ? 'dashboard.agents.favorite.remove'
                    : 'dashboard.agents.favorite.add',
                  { name: bot.chatbot_name },
                )}
                onClick={() => onToggleFavorite(bot)}
              >
                <svg viewBox="0 0 24 24" width="16" height="16"
                     fill={isFavorite ? 'currentColor' : 'none'}
                     stroke="currentColor" strokeWidth="1.8"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8-4.2-4.1 5.9-.9z" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>

      {visible.length === 0 && (
        <p className="ag-empty">{t('dashboard.agents.tabs.empty')}</p>
      )}
    </div>
  );
}
